import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { NutritionAppointment } from '@/types';

interface NutritionMonthCalendarProps {
  appointments: NutritionAppointment[];
  selectedDate: Date | null;
  onSelectDate: (date: Date) => void;
  /** Días de la semana con citas agendadas, para resaltarlos. */
  weekStartsOn?: 0 | 1;
}

const WEEKDAYS_MONDAY_FIRST = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * Vista mensual de la agenda de citas de nutrición.
 * Muestra un punto en los días que tienen citas y permite seleccionar el día
 * para ver su detalle en el panel lateral.
 */
export function NutritionMonthCalendar({
  appointments,
  selectedDate,
  onSelectDate,
  weekStartsOn = 1,
}: NutritionMonthCalendarProps) {
  const [month, setMonth] = useState<Date>(() => {
    const base = selectedDate ? new Date(selectedDate) : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const daysWithAppointments = useMemo(() => {
    const set = new Set<string>();
    appointments.forEach((a) => {
      const d = new Date(a.scheduled_at);
      set.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
    });
    return set;
  }, [appointments]);

  const cells = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const offset = (first.getDay() - weekStartsOn + 7) % 7;
    const start = new Date(first);
    start.setDate(first.getDate() - offset);

    return Array.from({ length: 42 }, (_, i) => {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      return date;
    });
  }, [month, weekStartsOn]);

  const today = new Date();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
          title="Mes anterior"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <p className="text-sm font-semibold">
          {MONTH_NAMES[month.getMonth()]} {month.getFullYear()}
        </p>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
          title="Mes siguiente"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS_MONDAY_FIRST.map((label, i) => (
          <div key={label} className="pb-1 text-[11px] font-semibold uppercase text-muted-foreground">
            {label}
          </div>
        ))}

        {cells.map((date, index) => {
          const inMonth = date.getMonth() === month.getMonth();
          const isSelected = selectedDate ? sameDay(date, selectedDate) : false;
          const isToday = sameDay(date, today);
          const hasAppointments = daysWithAppointments.has(
            `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
          );

          return (
            <button
              key={index}
              type="button"
              onClick={() => {
                onSelectDate(date);
                if (date.getMonth() !== month.getMonth()) {
                  setMonth(new Date(date.getFullYear(), date.getMonth(), 1));
                }
              }}
              className={cn(
                'relative flex h-10 flex-col items-center justify-center rounded-lg text-sm transition-colors',
                inMonth ? 'text-foreground' : 'text-muted-foreground/40',
                isSelected && 'bg-primary text-primary-foreground hover:bg-primary',
                !isSelected && isToday && 'bg-accent',
                !isSelected && 'hover:bg-accent/60'
              )}
            >
              <span>{date.getDate()}</span>
              {hasAppointments && (
                <span
                  className={cn(
                    'absolute bottom-1 h-1.5 w-1.5 rounded-full',
                    isSelected ? 'bg-primary-foreground' : 'bg-primary'
                  )}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
